<template>
  <div>
    <!-- allow on local/staging at all times, check browser in production only-->
    &nbsp;
    <!-- hidden content required to prevent bug with vue-transition -->

    <!-- signup form -->
    <v-col class="d-flex align-center">
      <v-card class="panel pa-6" :class="hasSignedUp ? 'signedUp' : ''">
        <div>
          <div v-if="isSignupShown" class="text-h4">
            Create an Account
          </div>
          <v-form v-if="!hasSignedUp" ref="termForm" lazy-validation>
            <v-checkbox
              v-model="isTermsCheckboxSelected"
              :rules="[rules.checkboxRequired]"
              :disabled="!isSignupShown"
            >
              <template v-slot:label>
                <div class="caption">
                  I have read and agree to the
                  <a
                    target="_blank"
                    href="https://adacado.com/support/terms-of-service/"
                    @click.stop
                  >
                    terms and conditions
                  </a>
                </div>
              </template>
            </v-checkbox>
          </v-form>
          <template v-if="isSignupShown">
            <v-btn
              @click="loginWithGoogle"
              class="white--text mb-4"
              color="blue darken-1"
              small
              :loading="showLoggingInWithGoogleLoader"
              style="font-weight:5000"
            >
              <svg
                class="authSVG"
                role="img"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke="white"
                  fill="white"
                  d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"
                />
              </svg>
              Sign up with Google
            </v-btn>
            <div><b>Or, provide an email and password</b></div>
            <v-form class="mt-4" ref="signupForm" lazy-validation style="width:100%">
              <!-- honey pot field (invisible to humans, but bots will probably fill it out) -->
              <!-- setting autocomplete to off doesn't work, so anything other than off or a recognizable keyword should be fine to prevent autofill -->
              <v-text-field
                label="phone number"
                v-model="phoneNumber"
                class="phoneNumberField"
                autocomplete="autofilling-this-is-no-bueno"
              ></v-text-field>

              <v-text-field
                outlined
                label="name@companyname.com"
                v-model="email"
                :rules="[rules.email]"
                v-bind="{ success: vemail }"
                required
                dense
              ></v-text-field>

              <v-text-field
                outlined
                label="Password*"
                v-model="password"
                :rules="[rules.required, rules.counter]"
                :type="showPW ? 'text' : 'password'"
                v-bind="{ success: passVerified }"
                required
                autocomplete="new-password"
                dense
              >
                <template v-slot:append>
                  <v-btn
                    :height="24"
                    :min-width="0"
                    text
                    class="px-0"
                    @click="
                      guibus_publish('dispatch:beacon', {
                        eventId: `welcome-sign_up_form_enter_password_show_password_button-click${
                          isMobileSized ? '-mobile' : ''
                        }`,
                      })
                      showPW = !showPW
                    "
                    tabindex="-1"
                  >
                    <v-icon v-if="!showPW" color="rgba(0,0,0,.54)">mdi-eye</v-icon>
                    <v-icon v-if="showPW" color="rgba(0,0,0,.54)">mdi-eye-off</v-icon>
                  </v-btn>
                </template>
              </v-text-field>

              <v-text-field
                outlined
                label="Verify Password*"
                v-model="vpassword"
                :rules="[rules.required, rules.counter, rules.match]"
                :type="showPW ? 'text' : 'password'"
                v-bind="{ success: passVerified }"
                required
                dense
              >
                <template v-slot:append>
                  <v-btn
                    :height="24"
                    :min-width="0"
                    text
                    class="px-0"
                    @click="
                      guibus_publish('dispatch:beacon', {
                        eventId: `welcome-sign_up_form_verify_password_show_password_button-click${
                          isMobileSized ? '-mobile' : ''
                        }`,
                      })
                      showPW = !showPW
                    "
                    tabindex="-1"
                  >
                    <v-icon v-if="!showPW" color="rgba(0,0,0,.54)">mdi-eye</v-icon>
                    <v-icon v-if="showPW" color="rgba(0,0,0,.54)">mdi-eye-off</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <v-btn
                class="white--text"
                color="blue darken-2 mb-1"
                :disabled="!emailAndPasswordValidated"
                @click="handleEmailSignup"
                :loading="showSignupLoader"
                small
              >
                Sign up with email and password
              </v-btn>
            </v-form>
          </template>
        </div>
        <div v-if="!isSignupShown">
          <div :class="isMobileSized ? 'text-h5' : 'text-h4'">
            Account Created for <b class="signedUpEmail">{{ email }}</b>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- Robot Check using Google invisible reCaptcha v2-->
    <!-- the recaptcha badge displays on bottom left of screen -->
    <!-- For the invisble reCaptcha there is no checkbox, user does not require to click on the checkbox
              when a user clicks on the signup button, only suspicous user will get a challenge
              For testing: you can trigger recaptcha challenge by using incognito mode
            -->
    <!-- Note: to develop reCaptcha feature you can see reCaptcha configurations
              here: https://www.google.com/recaptcha/admin/site/719889268/setup (part of adacado-prd Google Cloud project)
            -->
    <VueRecaptcha
      ref="recaptcha"
      :loadRecaptchaScript="true"
      @verify="onCaptchaVerified"
      @expired="onCaptchaExpired"
      size="invisible"
      badge="bottomleft"
      sitekey="6Ld0o-gqAAAAAFqaEesV-kXUD-s_l9hzxiUCZ5Uy"
    />

    <!-- domain exists modal -->
    <v-dialog v-model="isDomainExistsModalOpen" :max-width="'800px'" persistent>
      <v-card class="pa-6">
        <v-card-title class="display-1 justify-center"
          >An account with this domain exists</v-card-title
        >
        <v-card-text class="text-md-center">
          There is an existing account in our system associated with the business domain in your
          email address. If you are looking to join a team, please click 'Cancel' and contact your
          team to ask for an invite. If you wish to create a new and separate account, click 'New
          Account'. Please note that if you do create a new and separate account, you will be
          creating a new organization separate from the existing organization (billing will be
          separate, advertisers will not be shared, etc.).
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="white--text" color="blue darken-2" @click="handleNewAccountClick">
            New Account
          </v-btn>
          <v-btn
            dark
            class="ml-4"
            color="grey lighten-1"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: `welcome-sign_up_form_domain_exists_modal_cancel_button-click${
                  isMobileSized ? '-mobile' : ''
                }`,
              })
              handleCancelNewAccountClick
            "
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AccountPreloginInterfaceFactory from 'bs-interface-account/bs-interface-account-prelogin.js'
import FirebaseService from 'firebase-service'
import VueRecaptcha from 'vue-recaptcha'
import env from 'env-service'

export default {
  name: 'signUp',
  components: {
    VueRecaptcha,
  },
  props: {
    isAgency: {
      type: Boolean,
      default: false,
    },
    advertiserData: {
      type: Object,
      default: undefined,
    },
    isMobileSized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPW: false,
      email: '',
      companyName: '',
      password: '',
      vpassword: '',
      phoneNumber: '', // used strictly as a bot honey pot
      rules: {
        required: value => !!value || 'Required.',
        counter: value => (value != null && value.length >= 6) || 'Min 6 characters',
        email: () => {
          if (this.vemail) {
            return true
          } else {
            return 'Invalid e-mail.'
          }
        },
        match: () => {
          if (this.passVerified) {
            return true
          } else {
            return 'Passwords do not match'
          }
        },
        checkboxRequired: () =>
          this.isTermsCheckboxSelected ||
          'Please agree to the terms and conditions before proceeding',
      },

      currentBrowserName: undefined,
      currentBrowserVersion: undefined,
      showSignupLoader: false,
      showLoggingInWithGoogleLoader: false,
      isTermsCheckboxSelected: false,

      // state of recaptcha
      recaptchaToken: undefined,

      isProductionEnv: env.IS_PRODUCTION_CONNECTION,
      isSignupShown: this.$accountState === 'ACCOUNT_EXISTS' ? false : true,

      isDomainExistsModalOpen: false,
      isDomainExists: false,
      isDomainExistsInterruptResolveFunction: undefined,
      isDomainExistsCreateNewAccount: false,

      account: {
        email: '',
        properties: {
          FirstName: 'active user',
          LastName: 'not set',
          CompanyName: this.advertiserData.Name,
          packageName: 'ModelV2Advertiser', // Hardcode for now
          signupOptimizations: {
            version: 3,
            isAgency: this.isAgency,
          },
          Address: {
            StreetAddress1: this.advertiserData.Properties.Address.StreetAddress1,
            StreetAddress2: this.advertiserData.Properties.Address.StreetAddress2,
            Country: this.advertiserData.Properties.Address.Country,
            StateProvince: this.advertiserData.Properties.Address.StateProvince,
            City: this.advertiserData.Properties.Address.City,
            TimeZone: this.advertiserData.Properties.Address.TimeZone,
            ZipPostal: this.advertiserData.Properties.Address.ZipPostal,
          },
        },
        offerId: 2, // freemium
      },
      advertiserForm: {
        advertiserUrl: 'https://www.adacado.com',
        advertiserVertical: 'retail',
        advertiserIabVerticals: [0],
      },

      isSigningUpWithGoogle: false,
      hasSignedUp: false,
    }
  },
  methods: {
    handleEmailSignup() {
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-sign_up_form_email_signup_button-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      this.isSigningUpWithGoogle = false
      this.createNewAccount()
    },
    onCaptchaVerified(response) {
      // update recaptcha token when it is verified
      if (response) {
        this.recaptchaToken = response
        this.createNewAccount()
      }
    },
    onCaptchaExpired() {
      // reset recaptcha token when it is expired
      this.$refs.recaptcha.reset()
      this.recaptchaToken = undefined
    },
    async createNewAccount() {
      if (
        (this.isSignupShown && !this.$refs.signupForm.validate()) ||
        !this.$refs.termForm.validate()
      ) {
        return
      }

      this.showSignupLoader = true

      if (!this.recaptchaToken) {
        // execute invisible recaptcha
        this.$refs.recaptcha.execute()
        return
      }
      // todo: validate the recaptcha token on backend service
      // Check if domain exists (prevent users who are trying to join an existing billing account (multi-user)
      // from accidentally creating a brand new billing account)
      await this.AccountPreloginInterface.checkDomainExists(btoa(this.email)).then(response => {
        // expect response value: true/false as string
        this.isDomainExists = response === 'true'
      })
      if (this.isDomainExists) {
        // wait until the user confirms that they want to create a new account or cancel
        await new Promise(res => {
          this.isDomainExistsCreateNewAccount = false
          this.isDomainExistsModalOpen = true
          this.isDomainExistsInterruptResolveFunction = res
        })
        if (!this.isDomainExistsCreateNewAccount) {
          // user has clicked cancel, meaning they don't want to create a new account, so don't continue
          this.showSignupLoader = false
          this.showLoggingInWithGoogleLoader = false
          return
        }
      }

      if (this.isSignupShown) {
        // createUserWithEmailAndPassword only continues into the ".then" when the user does not exist in firebase
        FirebaseService.auth()
          .createUserWithEmailAndPassword(this.email, this.password)
          .then(
            () => {
              this.handleSuccessfulOauthLogin()
            },
            // fail: User already exists: We need the user to login. If they login and registration is incomplete, they will be back in this registration process as a redirect from the login
            () => {
              this.$emit('setSystemMessage', {
                isShownImmediately: true,
                type: 'error',
                message:
                  'This email address is already in use. If you already have an account, please use login',
              })
              this.showSignupLoader = false
            }
          )
      } else {
        // signup using google
        this.handleSuccessfulOauthLogin()
      }
    },
    handleNewAccountClick() {
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-sign_up_form_domain_exists_modal_new_account_button-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      this.isDomainExistsCreateNewAccount = true
      this.handleCancelNewAccountClick()
    },
    handleCancelNewAccountClick() {
      this.isDomainExistsModalOpen = false
      this.isDomainExistsInterruptResolveFunction()
    },
    loginWithGoogle() {
      if (!this.$refs.termForm.validate()) return
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-sign_up_form_google_signup_button-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      this.isSigningUpWithGoogle = true
      this.showLoggingInWithGoogleLoader = true
      var provider = new FirebaseService.auth.GoogleAuthProvider()
      FirebaseService.auth()
        .signInWithPopup(provider)
        .then(result => {
          if (!result.additionalUserInfo.isNewUser) {
            this.$emit('setSystemMessage', {
              isShownImmediately: true,
              type: 'error',
              message:
                'This email address is already in use. If you already have an account, please use login',
            })
            this.handleFailedOauthLogin()
            return
          }
          window.Vue.prototype.$firebaseUser = result.user
          this.email = result.user.email
          this.showLoggingInWithGoogleLoader = false
          this.isSignupShown = false
          this.createNewAccount()
        })
        .catch(err => {
          this.handleFailedOauthLogin()
          this.$emit('setSystemMessage', {
            isShownImmediately: true,
            type: 'error',
            message:
              'Google login failed. Please try again. Contact support if the problem persists.',
          })
          console.error('Google login failed. Error:', err)
        })
    },
    async handleSuccessfulOauthLogin() {
      // reset BSCS global
      window.adacado.bscs = undefined

      // set global $firebaseUser
      const firebaseUser = FirebaseService.auth().currentUser
      if (!window.Vue.prototype.$firebaseUser) {
        window.Vue.prototype.$firebaseUser = firebaseUser
      }
      // Initialize BSCS singleton with Firebase User, BSIs no longer need Firebase User
      window.adacadoBSCS.install({ firebaseUser })

      this.account.email = firebaseUser.email
      if (this.companyName) {
        this.account.properties.CompanyName = this.companyName
      }
      if (this.phoneNumber) {
        this.account.properties.PhoneNumberHoneyPot = this.phoneNumber
      }
      await this.AccountInterface.create(this.account).then(payload => {
        const { responseData } = payload
        this.createAccountCallback(responseData)
        // event tracking
        this.guibus_publish('dispatch:beacon', {
          eventId: `welcome-sign_up_form_${
            this.isSigningUpWithGoogle ? 'google_account' : 'email_account'
          }-created${this.isMobileSized ? '-mobile' : ''}`,
        })
      })
    },
    handleFailedOauthLogin() {
      FirebaseService.auth().signOut()
      this.showLoggingInWithGoogleLoader = false
    },
    async createAccountCallback(response) {
      // Responses to handle:
      // ACCOUNT_REGISTERED - user signed up successfully, or user has signed up before, publish success to parent
      // anything else = bug, show error message
      switch (response) {
        case 'ACCOUNT_REGISTERED':
          if (!this.$firebaseUser.emailVerified) {
            // We do not check the error on this email verification send: If it fails, user can always resend themselves later
            this.$firebaseUser.sendEmailVerification()
          }
          this.showSignupLoader = false
          this.isSignupShown = false
          this.hasSignedUp = true
          this.$emit('userHasSignedUp')
          break
        default:
          this.showSignupLoader = false
          this.$emit('setSystemMessage', {
            isShownImmediately: true,
            type: 'error',
            message:
              'An error occurred. Please try again. Contact support if the problem persists.',
          })
          break
      }
    },
  },
  computed: {
    emailAndPasswordValidated() {
      return this.vemail && this.passVerified
    },
    passVerified() {
      if (this.vpassword == null || this.vpassword == '') {
        return false
      } else if (this.password == this.vpassword) {
        return true
      } else {
        return false
      }
    },
    vemail() {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(this.email)
    },
    currentYear() {
      return new Date().getFullYear()
    },
    AccountInterface() {
      return window.adacado.bscs ? window.adacado.bscs.AccountInterfaceFactory() : undefined
    },
    AccountPreloginInterface() {
      return AccountPreloginInterfaceFactory()
    },
  },
}
</script>

<style lang="scss" scoped>
.signUpMainWrapper {
  background: rgba(94, 0, 235, 0.24);
}

.signUpContainer {
  width: 100vw;
  margin-top: -24px;
  .signUpContent {
    display: flex;
    height: 100vh;
    position: relative;
    width: 100%;
    margin: auto;
    align-items: center;
    .signUpBox {
      display: flex;
      padding: 20px;
      width: 1600px;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.featurePreviewImage {
  width: 85%;
}

.adacadoLogo {
  position: fixed;
  margin-left: 40px;
}

.panel {
  width: 400px;
  height: 476px;
  min-width: 400px;
  text-align: center;
  * {
    transition: all 1.5s cubic-bezier(0.25, 0.46, 0, 0.99);
  }
}

.panel.signedUp {
  height: 160px;
}

.fadeInSlideUp-enter-active,
.fadeInSlideUp-leave-active {
  transition: all 1s cubic-bezier(0.25, 0.46, 0, 0.99);
  transform: translateY(0%);
}
.fadeInSlideUp-enter,
.fadeInSlideUp-leave-to {
  transform: translateY(10%);
  opacity: 0;
}

.authSVG {
  width: 14px;
  height: 14px;
  margin-right: 15px;
}

.footerWrapper {
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: 0 0 5px 85px;
}

.phoneNumberField {
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  height: 0;
  width: 0;
  z-index: -1;
}

.newFeatureText {
  color: #5e00eb;
}

.signedUpEmail {
  font-size: 14px;
}

@media (max-width: 1280px) {
  .container {
    max-width: 1076px;
  }
}

@media (max-width: 1086px) {
  .container {
    max-width: 928px;
  }
  .panel {
    width: 350px;
    min-width: 350px;
  }
}

@media (max-height: 785px) {
  .signUpContainer .signUpContent .signUpBox {
    margin-top: 68px;
  }
  .featurePreviewImage {
    width: 60%;
  }
}
</style>
